"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Repository = exports.isExtendedError = void 0;
const tslib_1 = require("tslib");
const client_utils_1 = require("@sensenet/client-utils");
const BypassAuthentication_1 = require("../Authentication/BypassAuthentication");
const SchemaStore_1 = require("../Schemas/SchemaStore");
const AllowedChildTypes_1 = require("./AllowedChildTypes");
const ConstantContent_1 = require("./ConstantContent");
const ODataUrlBuilder_1 = require("./ODataUrlBuilder");
const Preview_1 = require("./Preview");
const RepositoryConfiguration_1 = require("./RepositoryConfiguration");
const Security_1 = require("./Security");
const Upload_1 = require("./Upload");
const Versioning_1 = require("./Versioning");
/**
 * Type guard to check if an error is extended with a response and a parsed body
 * @param e The error to check
 */
const isExtendedError = (e) => {
    return e.response ? true : false;
};
exports.isExtendedError = isExtendedError;
/**
 * Class that can be used as a main entry point to manipulate a sensenet content repository
 */
class Repository {
    constructor(config, fetchMethod = window && window.fetch && window.fetch.bind(window), schemas = new SchemaStore_1.SchemaStore()) {
        this.fetchMethod = fetchMethod;
        this.schemas = schemas;
        /**
         * Authentication service associated with the repository object
         */
        this.authentication = new BypassAuthentication_1.BypassAuthentication();
        /**
         * Shortcut for security- and permission-related custom actions
         */
        this.security = new Security_1.Security(this);
        /**
         * Shortcut for versioning related custom actions
         */
        this.versioning = new Versioning_1.Versioning(this);
        /**
         * Shortcut for upload related custom actions
         */
        this.upload = new Upload_1.Upload(this);
        /**
         * Shortcut for preview related custom actions
         */
        this.preview = new Preview_1.Preview(this);
        /**
         * Shortcut for allowedChildTypes related custom actions
         */
        this.allowedChildTypes = new AllowedChildTypes_1.AllowedChildTypes(this);
        this.configuration = Object.assign(Object.assign({}, RepositoryConfiguration_1.defaultRepositoryConfiguration), config);
        this.schemas.setSchemas(this.configuration.schemas);
    }
    /**
     * Disposes the Repository object
     */
    dispose() {
        this.authentication.dispose();
    }
    /**
     * Async method that will be resolved when the Repository is ready to make HTTP calls
     */
    awaitReadyState() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield Promise.all([this.authentication.checkForUpdate()]);
        });
    }
    /**
     * Wrapper for a native window.fetch method. The repository's readyState will be awaited and credentials will be included by default
     * @param {RequestInfo} input The RequestInfo object
     * @param {RequestInit} init Optional init parameters
     */
    fetch(input, init, awaitReadyState = true) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (awaitReadyState) {
                yield this.awaitReadyState();
            }
            const request = new Request(input, init);
            if (this.configuration.token) {
                request.headers.append('Authorization', `Bearer ${this.configuration.token}`);
            }
            return yield this.fetchMethod(request);
        });
    }
    /**
     * Gets a more meaningful error object from a specific response
     * @param response The Response object to extract the message
     */
    getErrorFromResponse(response) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            let msgFromBody = '';
            let body = {};
            let text = '';
            try {
                body = yield response.json();
                msgFromBody = body.error.message.value;
            }
            catch (error) {
                /** */
            }
            try {
                text = yield response.text();
            }
            catch (error) {
                /** */
            }
            const error = new Error(msgFromBody || text || response.statusText);
            error.body = body;
            error.response = response;
            error.text = text;
            error.statusCode = response.status;
            error.statusText = response.statusText;
            error.url = response.url;
            return error;
        });
    }
    /**
     * Loads a content from the content repository. If used with a fully qualified content path,
     * it will be transformed to an item path.
     * @param options Options for the Load request
     */
    load(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const contentPath = client_utils_1.PathHelper.getContentUrl(options.idOrPath);
            const params = ODataUrlBuilder_1.ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contentPath);
            const response = yield this.fetch(`${path}?${params}`, Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: 'GET' }));
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.json();
        });
    }
    /**
     * Loads a content collection from the repository
     * @param options Options for the Load request
     */
    loadCollection(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const params = ODataUrlBuilder_1.ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, options.path);
            const response = yield this.fetch(`${path}?${params}`, Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: 'GET' }));
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.json();
        });
    }
    /**
     * Returns the count of items in the requested collection.
     * The value depends on other optional query string parameters ($top, $skip, $filter, query, etc.) and does not depend on the $inlinecount parameter.
     */
    count(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const params = ODataUrlBuilder_1.ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, options.path);
            const response = yield this.fetch(`${path}/$count?${params}`, Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: 'GET' }));
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.json();
        });
    }
    /**
     * Posts a new content to the content repository
     * @param options Post request Options
     */
    post(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, options.parentPath);
            const params = ODataUrlBuilder_1.ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
            const postBody = Object.assign({}, options.content);
            postBody.__ContentType = options.contentType;
            postBody.__ContentTemplate = options.contentTemplate;
            const response = yield this.fetch(`${path}?${params}`, Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: 'POST', body: JSON.stringify(postBody) }));
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            if (response.status === 204) {
                return Promise.resolve({ d: null });
            }
            return yield response.json();
        });
    }
    /**
     * Updates an existing content in the repository using OData Patch
     * @param options Options for the Patch request
     */
    patch(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const contentPath = client_utils_1.PathHelper.getContentUrl(options.idOrPath);
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contentPath);
            const params = ODataUrlBuilder_1.ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
            const response = yield this.fetch(`${path}?${params}`, Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: 'PATCH', body: JSON.stringify(options.content) }));
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.json();
        });
    }
    /**
     * Updates an existing content in the repository using OData Put
     * @param options Options for the Put request
     */
    put(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const contentPath = client_utils_1.PathHelper.getContentUrl(options.idOrPath);
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contentPath);
            const params = ODataUrlBuilder_1.ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
            const response = yield this.fetch(`${path}?${params}`, Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: 'PUT', body: JSON.stringify(options.content) }));
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.json();
        });
    }
    createArray(param) {
        if (!(param instanceof Array)) {
            return [param];
        }
        return param;
    }
    /**
     * Deletes a content or a content collection from the Repository
     * @param options Options for the Delete request
     */
    delete(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.executeAction({
                idOrPath: ConstantContent_1.ConstantContent.PORTAL_ROOT.Path,
                method: 'POST',
                name: 'DeleteBatch',
                requestInit: options.requestInit,
                body: {
                    paths: this.createArray(options.idOrPath),
                    permanent: options.permanent,
                },
            });
        });
    }
    /**
     * Moves a content or content collection to a specified location
     * @param options Options for the Move request
     */
    move(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.executeAction({
                idOrPath: ConstantContent_1.ConstantContent.PORTAL_ROOT.Path,
                method: 'POST',
                name: 'MoveBatch',
                requestInit: options.requestInit,
                body: {
                    paths: this.createArray(options.idOrPath),
                    targetPath: options.targetPath,
                },
            });
        });
    }
    /**
     * Copies a content or content collection to a specified location
     * @param options Options for the Copy request
     */
    copy(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.executeAction({
                idOrPath: ConstantContent_1.ConstantContent.PORTAL_ROOT.Path,
                method: 'POST',
                name: 'CopyBatch',
                requestInit: options.requestInit,
                body: {
                    paths: this.createArray(options.idOrPath),
                    targetPath: options.targetPath,
                },
            });
        });
    }
    getPropertyValue(idOrPath, propertyName) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, client_utils_1.PathHelper.getContentUrl(idOrPath), propertyName, '$value');
            const response = yield this.fetch(path);
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.text();
        });
    }
    getMetadata(idOrPath) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const path = idOrPath === undefined
                ? client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, '$metadata')
                : client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, client_utils_1.PathHelper.getContentUrl(idOrPath), '$metadata');
            const response = yield this.fetch(`${path}?$format=json`);
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.json();
        });
    }
    /**
     * Shares a content or content collection with a specified
     * @param options Options for the Copy request
     */
    share(options) {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.executeAction({
                idOrPath: options.content.Id,
                method: 'POST',
                name: 'Share',
                body: {
                    token: options.identity.toString(),
                    content: options.content,
                    level: options.sharingLevel,
                    mode: options.sharingMode,
                    sendNotification: (_a = options.sendNotification) !== null && _a !== void 0 ? _a : true,
                },
            });
        });
    }
    /**
     * Retrieves a list of content actions for a specified content
     * @param options Options for fetching the Custom Actions
     */
    getActions(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const contextPath = client_utils_1.PathHelper.getContentUrl(options.idOrPath);
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contextPath, 'Actions');
            const response = yield this.fetch(`${path}${options.scenario ? `?scenario=${options.scenario}` : ''}`, {
                credentials: 'include',
                method: 'GET',
            });
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            return yield response.json();
        });
    }
    /**
     * Returns data for loading Office document for editing
     * @param idOrPath Id or path of the document
     */
    getWopiData(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.executeAction({
                idOrPath: options.idOrPath,
                method: 'GET',
                name: 'GetWopiData',
                requestInit: options.requestInit,
                oDataOptions: Object.assign(Object.assign({}, options.odataOptions), { action: options.action || 'edit' }),
            });
        });
    }
    /**
     * Executes a specified custom OData action
     * @param options Options for the Custom Action
     */
    executeAction(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const contextPath = client_utils_1.PathHelper.getContentUrl(options.idOrPath);
            let params = ODataUrlBuilder_1.ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
            const path = client_utils_1.PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contextPath, options.name);
            const requestOptions = Object.assign(Object.assign({}, options.requestInit), { credentials: 'include', method: options.method });
            if (options.method === 'POST') {
                requestOptions.body = JSON.stringify(options.body);
            }
            else {
                options.body &&
                    Object.keys(options.body).forEach((key) => (params += `&${key}=${encodeURIComponent(options.body[key])}`));
            }
            const response = yield this.fetch(`${path}?${params}`, requestOptions);
            if (!response.ok) {
                throw yield this.getErrorFromResponse(response);
            }
            if (response.status === 204) {
                return Promise.resolve({});
            }
            return yield response.json();
        });
    }
    /**
     * Reloads the content schemas from the sensenet backend
     * @returns {Promise<void>} A promise that will be resolved / rejected based on the action success
     */
    reloadSchema() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const schemas = yield this.executeAction({
                idOrPath: 'Root',
                name: 'GetSchema',
                method: 'GET',
                body: undefined,
            });
            this.schemas.setSchemas(schemas);
        });
    }
}
exports.Repository = Repository;
//# sourceMappingURL=Repository.js.map